import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Check } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Services = () => {
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, packagesRes] = await Promise.all([
          fetch(`${API}/services`),
          fetch(`${API}/packages`)
        ]);
        const servicesData = await servicesRes.json();
        const packagesData = await packagesRes.json();
        setServices(servicesData);
        setPackages(packagesData);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getPackagesForService = (serviceId) => {
    return packages.filter(p => p.service_id === serviceId);
  };

  const handleServiceClick = (service, e) => {
    e.preventDefault();
    const servicePackages = getPackagesForService(service.service_id);
    if (servicePackages.length > 0) {
      setSelectedService(service);
    } else {
      // If no packages, go to contact
      window.location.href = '/#contacto';
    }
  };

  return (
    <section id="servicios" className="py-20 md:py-32 bg-background" data-testid="services-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-widest text-muted-foreground block mb-4">
            Servicios
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mb-6">
            Lo Que Ofrezco
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Servicios fotográficos profesionales adaptados a tus necesidades, 
            con la máxima calidad y atención al detalle.
          </p>
        </motion.div>

        {/* Services Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {services.map((service, index) => (
              <motion.div
                key={service.service_id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group"
              >
                <button 
                  onClick={(e) => handleServiceClick(service, e)}
                  className="block w-full text-left"
                  data-testid={`service-${service.service_id}`}
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="font-serif text-lg md:text-xl text-white mb-1">{service.name}</h3>
                      <p className="text-xs text-white/70 line-clamp-2 hidden md:block">{service.description}</p>
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                      <span className="text-white text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center px-2">
                        {getPackagesForService(service.service_id).length > 0 ? 'Ver paquetes' : 'Solicitar información'}
                      </span>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <a href="/#contacto">
            <Button 
              className="rounded-none border border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background transition-colors duration-300 px-8 py-3 uppercase tracking-widest text-xs"
              data-testid="services-cta"
            >
              Solicitar Tarifas
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </motion.div>
      </div>

      {/* Packages Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelectedService(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-background border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-background z-10 p-6 border-b border-border flex items-center justify-between">
                <div>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Paquetes</span>
                  <h2 className="font-serif text-2xl md:text-3xl">{selectedService.name}</h2>
                </div>
                <button 
                  onClick={() => setSelectedService(null)}
                  className="p-2 hover:bg-muted rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Packages */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {getPackagesForService(selectedService.service_id)
                    .sort((a, b) => {
                      const order = { basic: 0, standard: 1, premium: 2 };
                      return (order[a.tier] || 0) - (order[b.tier] || 0);
                    })
                    .map((pkg, index) => (
                      <motion.div
                        key={pkg.package_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`border p-6 ${
                          pkg.tier === 'premium' 
                            ? 'border-foreground bg-foreground text-background' 
                            : 'border-border'
                        }`}
                      >
                        {/* Tier badge */}
                        <div className="text-xs uppercase tracking-widest mb-4 opacity-60">
                          {pkg.tier === 'basic' && 'Básico'}
                          {pkg.tier === 'standard' && 'Estándar'}
                          {pkg.tier === 'premium' && 'Premium'}
                        </div>

                        {/* Name */}
                        <h3 className="font-serif text-xl mb-2">{pkg.name}</h3>

                        {/* Description */}
                        {pkg.description && (
                          <p className={`text-sm mb-4 ${pkg.tier === 'premium' ? 'opacity-80' : 'text-muted-foreground'}`}>
                            {pkg.description}
                          </p>
                        )}

                        {/* Features */}
                        <ul className="space-y-2 mb-6">
                          {pkg.features?.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                pkg.tier === 'premium' ? 'text-background' : 'text-foreground'
                              }`} />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* Price placeholder */}
                        <div className={`text-center pt-4 border-t ${
                          pkg.tier === 'premium' ? 'border-background/20' : 'border-border'
                        }`}>
                          <a 
                            href="/#contacto"
                            onClick={() => setSelectedService(null)}
                            className={`text-sm uppercase tracking-widest hover:opacity-70 transition-opacity ${
                              pkg.tier === 'premium' ? 'text-background' : 'text-foreground'
                            }`}
                          >
                            Consultar tarifas →
                          </a>
                        </div>
                      </motion.div>
                    ))}
                </div>

                {/* Contact CTA */}
                <div className="mt-8 text-center">
                  <p className="text-muted-foreground mb-4">
                    ¿No encuentras lo que buscas? Puedo crear un paquete personalizado para ti.
                  </p>
                  <a href="/#contacto" onClick={() => setSelectedService(null)}>
                    <Button className="gap-2">
                      Contactar para presupuesto personalizado
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
